import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { QRCodeSVG } from 'qrcode.react';
import { CreditCard, QrCode, Copy, Clock, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';

const statusConfig = {
  aguardando_pagamento: { label: 'Aguardando', color: 'bg-amber-400/10 text-amber-400 border-amber-400/20' },
  pago: { label: 'Pago', color: 'bg-green-400/10 text-green-400 border-green-400/20' },
  atrasado: { label: 'Atrasado', color: 'bg-red-400/10 text-red-400 border-red-400/20' },
  cancelado: { label: 'Cancelado', color: 'bg-zinc-400/10 text-zinc-400 border-zinc-400/20' },
};

const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function MeuFinanceiroPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pixDialog, setPixDialog] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/gestor/meu-financeiro');
      setData(res.data);
    } catch { toast.error('Erro ao carregar dados'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const gerarPix = async (mensalidadeId) => {
    setPixLoading(true);
    try {
      const res = await api.post(`/gestor/meu-financeiro/${mensalidadeId}/gerar-pix`);
      setPixDialog(res.data);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    setPixLoading(false);
  };

  const copyPix = () => {
    if (pixDialog?.pix_code) {
      navigator.clipboard.writeText(pixDialog.pix_code);
      toast.success('Codigo PIX copiado!');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64 bg-[#141414]" />
        <Skeleton className="h-32 bg-[#141414]" />
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16 bg-[#141414]" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="meu-financeiro-page">
      <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">MEU PLANO</h1>

      {data?.plano && (
        <Card className="bg-[#007AFF]/5 border-[#007AFF]/20" data-testid="plano-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500">PLANO ATUAL</p>
                <p className="text-2xl font-black font-['Barlow_Condensed'] text-white mt-1">{data.plano.nome}</p>
                <p className="text-sm text-zinc-400">{data.plano.periodicidade} | {data.plano.descricao}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-black font-['Barlow_Condensed'] text-[#007AFF]">{fmt(data.plano.valor)}</p>
                <p className="text-xs text-zinc-500">/{data.plano.periodicidade}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <h2 className="text-lg font-bold font-['Barlow_Condensed'] tracking-tight uppercase text-white">MENSALIDADES DA PLATAFORMA</h2>

      {(!data?.mensalidades || data.mensalidades.length === 0) ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-8 text-center">
            <CreditCard className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">Nenhuma mensalidade da plataforma encontrada.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.mensalidades.map(m => {
            const cfg = statusConfig[m.status] || statusConfig.aguardando_pagamento;
            return (
              <Card key={m.id} className="bg-[#141414] border-[#27272A] hover:border-[#3f3f46] transition-all" data-testid={`meu-fin-${m.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${m.status === 'pago' ? 'bg-green-500/10' : m.status === 'atrasado' ? 'bg-red-500/10' : 'bg-amber-500/10'}`}>
                        {m.status === 'pago' ? <CheckCircle className="w-4 h-4 text-green-400" /> : m.status === 'atrasado' ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <Clock className="w-4 h-4 text-amber-400" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">Competencia {m.competencia}</p>
                        <p className="text-xs text-zinc-500">Vencimento: {m.data_vencimento}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-lg font-bold font-['Barlow_Condensed'] text-white">{fmt(m.valor)}</p>
                      <Badge className={cfg.color}>{cfg.label}</Badge>
                      {m.status !== 'pago' && m.status !== 'cancelado' && (
                        <Button
                          size="sm"
                          onClick={() => m.pix_code ? setPixDialog({ pix_code: m.pix_code, valor: m.valor }) : gerarPix(m.id)}
                          disabled={pixLoading}
                          className="bg-[#007AFF] hover:bg-[#0066DD]"
                          data-testid={`pagar-gestor-${m.id}`}
                        >
                          {pixLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4 mr-1" />}
                          PIX
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!pixDialog} onOpenChange={() => setPixDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2">
              <QrCode className="w-5 h-5 text-[#007AFF]" /> PIX - Plano da Plataforma
            </DialogTitle>
          </DialogHeader>
          {pixDialog && (
            <div className="space-y-4 text-center" data-testid="gestor-pix-dialog">
              <p className="text-2xl font-black font-['Barlow_Condensed'] text-white">{fmt(pixDialog.valor)}</p>
              <div className="bg-white p-4 rounded-lg inline-block mx-auto"><QRCodeSVG value={pixDialog.pix_code} size={200} /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Copia e Cola</p>
                <div className="flex gap-2">
                  <Input value={pixDialog.pix_code} readOnly className="bg-[#0A0A0A] border-[#27272A] text-xs" data-testid="gestor-pix-code-input" />
                  <Button size="icon" variant="outline" onClick={copyPix} className="border-[#27272A] shrink-0" data-testid="gestor-copy-pix-btn"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <p className="text-xs text-zinc-500">Aguardando confirmacao manual pelo Administrador.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
